import LFCChartsHeatMap from '_components/charts/LFCChartsHeatMap';
import LFCDataGrid from '_components/datagrid/LFCDataGrid';
import {ProgressAria} from '_components/ProgressAria';
import {useFormatWithSettings} from '_logics/LFCFormatUtil';
import {rendJudgeDisp} from '_logics/LFCRenderUtil';
import {getLFCData} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import {useSnackbar} from 'notistack';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Grid, useTheme, Button, Tooltip} from '@mui/material';
import {GridColDef, GridRenderCellParams} from '@mui/x-data-grid-pro';

import ImageDialog from './components/ImageDialog';
import BeadCrossSectionDialog from './components/BeadCrossSectionDialog';
import type {InspectionRow} from './types';

/**
 * 引数
 */
interface Props {
  uuid: string;
}

export const MapResultPanel = ({uuid}: Props) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const {fmtFloat, fmtInteger} = useFormatWithSettings();
  const [startProcess, setStartProcess] = useState<boolean>(false);
  const cancelSource = useRef<CancelTokenSource | null>(null);

  const [dsListData, setDsListData] = useState([]);
  const [dsColumns, setDsColumns] = useState<GridColDef[]>([]);
  const [dsRows, setDsRows] = useState<InspectionRow[]>([]);

  //ImageDig用
  const [imgOpen, setImgOpen] = React.useState(false);
  const handleImgClose = () => {
    setImgOpen(false);
  };
  const [showDetailPath, setShowDetailPath] = useState<string>('');

  const [imagePath, setImagePath] = useState<any>([]);
  const [dsRow, setDsRow] = useState<InspectionRow[]>([]);

  // Bead断面詳細ダイアログ用
  const [beadDialogOpen, setBeadDialogOpen] = useState(false);
  const [beadDialogRow, setBeadDialogRow] = useState<InspectionRow | null>(null);
  const openBeadDialog = (row: InspectionRow) => {
    setBeadDialogRow(row);
    setBeadDialogOpen(true);
  };
  const closeBeadDialog = () => {
    setBeadDialogOpen(false);
    setBeadDialogRow(null);
  };

  const theme = useTheme();

  /**
   * 画像セル定義
   * @param params
   * @returns
   */
  const imageFileUrl = (params: any) => {
    if (!imagePath || imagePath.length === 0 || !imagePath[0]?.svalue) {
      return <div>画像データ読み込み中...</div>;
    }
    const src = imagePath[0].svalue + params.formattedValue;

    const openImage = () => {
      setShowDetailPath(src);
      // 画像ダイアログの下部グリッドに選択行の属性を表示するために行データを渡す
      setDsRow([params.row]);
      setImgOpen(true);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openImage();
      }
    };

    return (
      <img
        src={src}
        width="100%"
        onClick={openImage}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="button"
        aria-label={t('画像を開く')}
        style={{cursor: 'pointer'}}
        alt=""
      />
    );
  };

  /**
   * 一覧のヘッダを生成
   * @param getList
   */
  const makeColumn = (getList: any) => {
    let tmp_key_0: any = Object.keys(getList[0]);
    let nullClean_data: any = {};

    getList.forEach((dataOneRecord: any) => {
      Object.values(dataOneRecord).forEach((item: any, index: number) => {
        if (tmp_key_0[index] === 'tolerance_type') {
          if (dataOneRecord.job_type_name === 'HoleCompare3DI') {
            nullClean_data[tmp_key_0[index]] = item; // null を含む
          } else if (item !== null) {
            nullClean_data[tmp_key_0[index]] = item; // null は除外
          }
        } else if (item !== null) {
          nullClean_data[tmp_key_0[index]] = item;
        }
      });
    });

    let tmp_key: any = Object.keys(nullClean_data);
    //re-arrange the table columns
    let itemsToMove = [
      'tolerance_type',
      'x_lower_limit',
      'x_upper_limit',
      'y_lower_limit',
      'y_upper_limit',
      'z_lower_limit',
      'z_upper_limit',
      'x_gap',
      'y_gap',
      'z_gap',
      'center_range_limit',
      'center_range_gap',
      'cylinder_radius_limit',
      'height_lower_limit',
      'height_upper_limit',
      'cylinder_radius_gap',
      'height_gap'
    ];
    // itemsToMove.reverse();
    tmp_key = [
      ...itemsToMove.filter((item: string) => tmp_key.includes(item)),
      ...tmp_key.filter((item: string) => !itemsToMove.includes(item))
    ];
    ///
    const baseCols: GridColDef[] = [
      ...tmp_key.map((item: string) => {
        switch (item) {
          case 'id':
            return {
              field: item,
              width: 100,
              hide: true
            };
          case 'img_filename':
            return {
              field: item,
              headerName: t('画像'),
              width: 160,
              disableColumnMenu: true,
              sortable: false,
              renderCell: imageFileUrl
            };
          case 'uuid':
            return {
              field: item,
              headerName: 'uuid',
              width: 160,
              disableColumnMenu: true,
              sortable: false,
              renderCell: null,
              hide: true
            };
          case 'serial':
            return {
              field: item,
              headerName: `${t('シリアルNo')}`,
              width: 160,
              disableColumnMenu: true,
              sortable: false,
              renderCell: null,
              hide: true
            };
          case 'lq_time':
            return {
              field: item,
              headerName: `${t('日時')}`,
              width: 160,
              disableColumnMenu: true,
              sortable: false,
              renderCell: null,
              hide: true
            };
          case 'job_type_name':
            return {
              field: item,
              headerName: t('ジョブ'),
              width: 160,
              disableColumnMenu: true,
              sortable: true,
              renderCell: null
            };
          case 'map':
            return {
              field: item,
              headerName: t('部位'),
              width: 80,
              disableColumnMenu: true,
              sortable: true,
              renderCell: null
            };
          case 'branch_no':
            return {
              field: item,
              headerName: 'SEQ',
              width: 60,
              disableColumnMenu: true,
              sortable: true,
              renderCell: null,
              align: 'right'
            };
          case 'judge':
            return {
              field: item,
              headerName: `${t('判定')}`,
              width: 65,
              disableColumnMenu: true,
              sortable: true,
              renderCell: (prms: GridRenderCellParams) => rendJudgeDisp(prms.value),
              align: 'center'
            };
          case 'tolerance_type':
            return {
              field: item,
              headerName: 'tolerance_type',
              width: 65,
              disableColumnMenu: true,
              sortable: true,
              renderCell: (prms: GridRenderCellParams) => {
                if (prms.value === 0 || prms.value === null) {
                  return 'Sphere';
                } else if (prms.value === 1) {
                  return 'Cube';
                } else if (prms.value === 2) {
                  return 'Cylinder';
                }
              },
              align: 'center'
            };
          default:
            return {
              field: item,
              headerName: item,
              width: 100,
              disableColumnMenu: true,
              sortable: false,
              renderCell: (prms: GridRenderCellParams) => {
                if (prms.value == null) {
                  return '-';
                }

                // 数値かどうかをチェック
                const numValue = Number(prms.value);
                if (isNaN(numValue)) {
                  return prms.value;
                }

                // 小数点があるかどうかを判定
                const hasDecimal = numValue % 1 !== 0;

                if (hasDecimal) {
                  // 小数点がある場合はfmtFloatを使用
                  return fmtFloat(numValue);
                } else {
                  // 整数の場合はfmtIntegerを使用
                  return fmtInteger(numValue);
                }
              },
              // 実際のデータ値を確認して数値型かどうかを判断
              align: !isNaN(Number(getList[0][item])) ? 'right' : 'left'
            };
        }
      })
    ];

    // BeadAnalysis の場合は固定列「ビード断面詳細」を一番左へ追加（デフォルト最左）
    const isBead = getList?.[0]?.job_type_name === 'BeadAnalysis';
    const cols = [...baseCols];
    if (isBead) {
      const beadDetailCol: GridColDef = {
        field: 'bead_detail',
        headerName: t('ビード断面詳細'),
        width: 130,
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: any) => (
          <Tooltip title={t('詳細')} placement="top" arrow>
            <span>
              {/* DataGrid 内で disabled でも Tooltip を有効にするため span ラッパー */}
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={
                  <img
                    src="/assets/icons/bead-detail.svg"
                    alt="detail"
                    width={16}
                    height={16}
                    style={{display: 'block'}}
                  />
                }
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation(); // 行クリックの連鎖を止める
                  openBeadDialog(params.row);
                }}
              >
                {t('詳細')}
              </Button>
            </span>
          </Tooltip>
        )
      };
      const imgIdx = cols.findIndex(c => c.field === 'img_filename');
      if (imgIdx >= 0) {
        cols.splice(imgIdx + 1, 0, beadDetailCol);
      } else {
        cols.unshift(beadDetailCol);
      }
    }

    setDsColumns(cols);
  };

  const [selectedLine, setSelectedLine] = useState({y: ''});

  /**
   * 【検査x部位】クリックイベント
   * @param params
   */
  const onClickHeat = (params: {x: string; y: string}) => {
    setSelectedLine({y: params.y});

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    const isBead = params.y === 'BeadAnalysis';
    const sqlId = isBead ? 169 : 71;
    Promise.allSettled([
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: sqlId,
        parameters: {
          uuid: uuid,
          job_type_name: params.y,
          map: null
        },
        name: isBead ? `${t('ビード明細')}` : `${t('検査結果_検査の一覧_部位毎')}`,
        cancelToken: cancelSource.current.token,
        t
      }).then((datas: any) => {
        if (datas.length > 0) {
          makeColumn(datas);
          // map が数値と文字列の混在でも自然順で並ぶように numeric オプション付き localeCompare を使用
          setDsRows(
            datas.sort((a: any, b: any) =>
              String(a?.map ?? '').localeCompare(String(b?.map ?? ''), undefined, {numeric: true})
            )
          );
        } else {
          setDsColumns([]);
          setDsRows([]);
        }
      })
    ]).then(() => {
      setStartProcess(false);
    });
  };

  /**
   * 検索処理
   */
  useEffect(() => {
    if (uuid === '') return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      //検査結果_検査箇所×検査種別
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 70,
        parameters: {
          uuid: uuid
        },
        name: `${t('検査結果_検査箇所×検査種別')}`,
        cancelToken: cancelSource.current.token,
        t
      }).then((datas: any) => {
        setDsListData(
          datas.sort((a: any, b: any) => {
            // 文字列部分（数字以外の部分）を取得
            const textA = a.map.replace(/\d+/g, '');
            const textB = b.map.replace(/\d+/g, '');

            // 数字部分を取得
            const numA = parseInt(a.map.replace(/\D+/g, ''), 10) || 0; // 数字がない場合は 0 を使用
            const numB = parseInt(b.map.replace(/\D+/g, ''), 10) || 0;

            // まず文字列部分でソート
            const textCompare = textA.localeCompare(textB);
            if (textCompare !== 0) {
              return textCompare;
            }

            // 文字列部分が同じ場合、数値部分でソート
            return numA - numB;
          })
        );
      }),
      // 画像パス取得
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 131,
        parameters: {},
        ds_state: setImagePath,
        cancelToken: cancelSource.current.token,
        t
      })
    ]).finally(() => {
      setStartProcess(false);
    });
  }, [uuid]);

  useEffect(() => {
    return () => {
      // アンマウント処理
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  return (
    <ProgressAria startProcess={startProcess}>
      <Grid container spacing={1}>
        <Grid item xs={12} sx={{maxHeight: '34vh', overflow: 'auto'}}>
          <LFCChartsHeatMap
            title={`${t('判定結果')}(${t('ジョブ')}×${t('部位')})`}
            source={dsListData}
            x={{dsColumn: 'map'}}
            y={{dsColumn: 'job_type_name'}}
            series="judge"
            visual_map={{
              min: 0,
              max: 1,
              show: false
            }}
            onClick={onClickHeat}
            exportData={dsListData}
            exportFields={{
              serial: t('シリアルNo'),
              job_type_name: t('ジョブ'),
              map: t('部位'),
              judge: t('判定')
            }}
            exportFilename={`${t('判定結果')}(${t('検査')}×${t('部位')})`}
            selected={selectedLine}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            '.grid-css--1': {
              bgcolor: theme.palette.error.light
            }
          }}
        >
          {0 < dsColumns.length ? (
            <LFCDataGrid
              columns={dsColumns.filter((col: any) => col.headerName !== 'rn')}
              rows={dsRows}
              height="40vh"
              exportFilename={`${t('判定結果')}(${t('検査')}×${t('部位')})${t('明細')}`}
              columnOrder={[
                {name: 'job_type_name', order: 0},
                {name: 'map', order: 1},
                {name: 'branch_no', order: 2}
              ]}
              style={{cursor: 'default'}}
              initialState={{
                // 要件: 「ビード断面詳細」列は画像の右側に固定（スクロールさせない）。
                // そのため、左ピン留めの最後に 'bead_detail' を追加し、
                // 並びを ジョブ/部位/SEQ/判定/画像/ビード断面詳細 の順にする。
                left: ['job_type_name', 'map', 'branch_no', 'judge', 'img_filename', 'bead_detail'],
                right: ['actions']
              }}
              pageSize={50}
              getRowClassName={(params: any) => `grid-css--${params.row.judge}`}
            />
          ) : (
            <div
              style={{
                height: '40vh',
                textAlign: 'center',
                padding: '100px 0',
                border: 'solid 1px gray'
              }}
            >
              {t('↑をクリック')}
            </div>
          )}
        </Grid>
        <Grid item xs={12}>
          <ImageDialog
            open={imgOpen}
            onClose={handleImgClose}
            showDetailPath={showDetailPath}
            dsColumns={dsColumns.filter(col => col.field !== 'img_filename')}
            dsRow={dsRow}
          />
        </Grid>
        <Grid item xs={12}>
          <BeadCrossSectionDialog
            open={beadDialogOpen}
            onClose={closeBeadDialog}
            row={beadDialogRow}
            uuid={uuid}
            dsColumns={dsColumns.filter(col => col.field !== 'img_filename')}
          />
        </Grid>
      </Grid>
    </ProgressAria>
  );
};
